// Kataloğa yükselt — zorunlu bağımlılık zinciri (DR9/DR11/DR15/DR17-3). Gerçek `<table>` (kutusuz,
// `<th scope="col">`, 320 px gövde + yapışkan başlık); satırda Katalog adı + Yeni kod girdileri,
// Üst kategori metni + "değiştir" → satır içi SearchSelect. Enter göndermez. Hatalı satır: girdide
// `aria-invalid` + `aria-describedby` → altındaki `errorBox` satırı. Kırılım grubu/üyeleri sahibinin
// altında girintili özet satırı. ≤720 px: üye başına yığılmış satırlar (ad → kod → kategori → durum).

import { Fragment, useState, type CSSProperties, type KeyboardEvent } from "react";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import {
  defaultPromotedCode,
  type PromoteChainOverride,
  type PromoteChainRow,
} from "../../lib/kpi-promote-form";
import { SearchSelect, type SearchSelectOption } from "../common/SearchSelect";
import {
  amberBox,
  codeStyle,
  errorBox,
  hitArea44,
  inputStyle,
  monoStyle,
  tdStyle,
  thStyle,
} from "./kpi-form-styles";

const P = KPI_PACKAGES_COPY.promote;

/** Kod girdisi kimliği — "satıra git" ve 409 sonrası odak buraya (kök başlık alanı da aynı kalıp). */
export const promoteCodeInputId = (metricId: string): string => `promote-code-${metricId}`;
export const promoteRowErrorId = (metricId: string): string => `promote-err-${metricId}`;
/** Kırılım grubu/üyesi hatası sahibin özet satırında — "satıra git" bu satıra odaklanır. */
export const promoteBreakdownId = (ownerId: string): string => `promote-bd-${ownerId}`;
export const promoteBreakdownErrorId = (ownerId: string): string => `promote-bd-err-${ownerId}`;

/** Enter formu göndermez (DR17-3) — pencerede form yok ama tarayıcı/üst dinleyiciye sızmasın. */
export function blockEnter(e: KeyboardEvent<HTMLInputElement>): void {
  if (e.key === "Enter") e.preventDefault();
}

export interface PromoteChainTableProps {
  rows: readonly PromoteChainRow[];
  breakdownCount: ReadonlyMap<string, number>;
  overrides: Readonly<Record<string, PromoteChainOverride>>;
  /** başlıkta seçilen üst — satır düzeltmesi yoksa satırın üstü */
  rootParentId: string;
  categoryOptions: readonly SearchSelectOption[];
  errors: ReadonlyMap<string, string>;
  /** sahip → kırılım grubu/üyesi satır hatası (özet satırında) */
  breakdownErrors: ReadonlyMap<string, string>;
  nameWarning: (metricId: string) => string | null;
  narrow: boolean;
  disabled: boolean;
  onChange: (metricId: string, patch: PromoteChainOverride) => void;
}

const smallInput: CSSProperties = { ...inputStyle, minHeight: 44, fontSize: "0.875rem" };
const subText: CSSProperties = { fontSize: "0.875rem", color: "var(--color-text-3)" };
const linkBtn: CSSProperties = {
  ...hitArea44,
  margin: 0,
  padding: "0 6px",
  color: "var(--color-primary-text)",
  fontSize: "0.875rem",
  textDecoration: "underline",
};

/**
 * Sahibin kırılım özet satırı ("+N kırılım üyesi …"); grup/üye kod hatası varsa satır `errorBox` +
 * `aria-invalid` + `aria-describedby` taşır ve odaklanabilir (tabIndex −1, "satıra git" hedefi).
 */
export function BreakdownSummary(props: {
  ownerId: string;
  count: number;
  error: string | undefined;
}): JSX.Element | null {
  const { ownerId, count, error } = props;
  if (count === 0 && !error) return null;
  if (!error)
    return (
      <div style={{ ...subText, paddingLeft: 20 }} data-testid="promote-breakdown-summary">
        {P.memberSummary(count)}
      </div>
    );
  return (
    <div
      id={promoteBreakdownId(ownerId)}
      tabIndex={-1}
      aria-invalid="true"
      aria-describedby={promoteBreakdownErrorId(ownerId)}
      data-testid="promote-breakdown-summary"
      style={{ paddingLeft: 20, display: "grid", gap: 4 }}
    >
      {count > 0 ? <span style={subText}>{P.memberSummary(count)}</span> : null}
      <div id={promoteBreakdownErrorId(ownerId)} style={{ ...errorBox, whiteSpace: "pre-line" }}>
        {error}
      </div>
    </div>
  );
}

export function PromoteChainTable(props: PromoteChainTableProps): JSX.Element {
  const { rows, breakdownCount, overrides, errors, narrow, disabled } = props;
  const [editingParent, setEditingParent] = useState<string | null>(null);
  const caption = P.chainTitle(rows.length);
  const parentOf = (r: PromoteChainRow): string =>
    overrides[r.metricId]?.parentId ?? props.rootParentId;
  const parentLabel = (id: string): string =>
    props.categoryOptions.find((o) => o.value === id)?.label ?? P.parentEmpty;

  const labelInput = (r: PromoteChainRow): JSX.Element => (
    <input
      style={smallInput}
      aria-label={P.rowLabel(r.label)}
      value={overrides[r.metricId]?.label ?? r.label}
      placeholder={r.label}
      disabled={disabled}
      maxLength={200}
      onKeyDown={blockEnter}
      onChange={(e) => props.onChange(r.metricId, { label: e.target.value })}
    />
  );
  const codeInput = (r: PromoteChainRow): JSX.Element => {
    const err = errors.get(r.metricId);
    return (
      <input
        id={promoteCodeInputId(r.metricId)}
        style={{
          ...smallInput,
          ...monoStyle,
          ...(err ? { borderColor: "var(--color-error)" } : {}),
        }}
        aria-label={P.rowCode(r.label)}
        value={overrides[r.metricId]?.newCode ?? r.newCode}
        placeholder={defaultPromotedCode(r.code)}
        disabled={disabled}
        maxLength={64}
        onKeyDown={blockEnter}
        {...(err
          ? { "aria-invalid": true, "aria-describedby": promoteRowErrorId(r.metricId) }
          : {})}
        onChange={(e) => props.onChange(r.metricId, { newCode: e.target.value })}
      />
    );
  };
  const parentCell = (r: PromoteChainRow): JSX.Element =>
    editingParent === r.metricId ? (
      <SearchSelect
        ariaLabel={P.rowParent(r.label)}
        value={parentOf(r)}
        options={props.categoryOptions}
        emptyLabel={P.parentEmpty}
        size="sm"
        showCode={false}
        disabled={disabled}
        onChange={(v) => {
          props.onChange(r.metricId, { parentId: v });
          setEditingParent(null);
        }}
      />
    ) : (
      <span className="flex items-center gap-1" style={{ flexWrap: "wrap" }}>
        <span>{parentLabel(parentOf(r))}</span>
        <button
          type="button"
          style={linkBtn}
          disabled={disabled}
          aria-label={`${P.rowParent(r.label)} — ${P.changeParent}`}
          onClick={() => setEditingParent(r.metricId)}
        >
          {P.changeParent}
        </button>
      </span>
    );
  const indicatorCell = (r: PromoteChainRow): JSX.Element => (
    <>
      <span>{r.label}</span>
      <span style={codeStyle}>{r.code}</span>
    </>
  );
  const notes = (r: PromoteChainRow): JSX.Element[] => {
    const out: JSX.Element[] = [];
    const err = errors.get(r.metricId);
    if (err)
      out.push(
        <div
          key="e"
          id={promoteRowErrorId(r.metricId)}
          style={{ ...errorBox, whiteSpace: "pre-line" }}
        >
          {err}
        </div>,
      );
    const warn = props.nameWarning(r.metricId);
    if (warn)
      out.push(
        <div key="w" style={amberBox}>
          {warn}
        </div>,
      );
    const n = breakdownCount.get(r.metricId) ?? 0;
    const bdErr = props.breakdownErrors.get(r.metricId);
    if (n > 0 || bdErr)
      out.push(<BreakdownSummary key="b" ownerId={r.metricId} count={n} error={bdErr} />);
    return out;
  };

  const heading = (
    <span className="text-sm" style={{ fontWeight: 600, color: "var(--color-text)" }}>
      {caption}
    </span>
  );

  if (narrow) {
    return (
      <section aria-label={caption} data-testid="promote-chain-stacked">
        <div style={{ marginBottom: 8 }}>{heading}</div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 320, overflowY: "auto" }}>
          {rows.map((r) => (
            <li
              key={r.metricId}
              className="grid gap-2"
              style={{ padding: "10px 0", borderBottom: "1px solid var(--color-border)" }}
            >
              <div className="text-sm">{indicatorCell(r)}</div>
              {labelInput(r)}
              {codeInput(r)}
              <div className="text-sm">{parentCell(r)}</div>
              {notes(r)}
            </li>
          ))}
        </ul>
      </section>
    );
  }

  const sticky: CSSProperties = {
    ...thStyle,
    position: "sticky",
    top: 0,
    background: "var(--color-surface)",
    zIndex: 1,
  };
  return (
    <div style={{ maxHeight: 320, overflowY: "auto" }} data-testid="promote-chain-table">
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <caption style={{ textAlign: "left", paddingBottom: 8 }}>{heading}</caption>
        <thead>
          <tr>
            <th scope="col" style={{ ...sticky, width: "26%" }}>
              {P.chainHead.indicator}
            </th>
            <th scope="col" style={{ ...sticky, width: "28%" }}>
              {P.chainHead.label}
            </th>
            <th scope="col" style={{ ...sticky, width: "22%" }}>
              {P.chainHead.code}
            </th>
            <th scope="col" style={{ ...sticky, width: "24%" }}>
              {P.chainHead.parent}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const extra = notes(r);
            const cell: CSSProperties =
              extra.length > 0 ? { ...tdStyle, borderBottom: "none" } : tdStyle;
            return (
              <Fragment key={r.metricId}>
                <tr>
                  <td style={{ ...cell, overflowWrap: "anywhere" }}>{indicatorCell(r)}</td>
                  <td style={cell}>{labelInput(r)}</td>
                  <td style={cell}>{codeInput(r)}</td>
                  <td style={cell}>{parentCell(r)}</td>
                </tr>
                {extra.length > 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...tdStyle, paddingTop: 0 }}>
                      <div className="grid gap-1">{extra}</div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
